import { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const empty = { code: "", name: "", default_message: "" };

export default function AdminLanguages() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const load = () => api.get("/languages").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/admin/languages", form); toast.success("Language added"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/languages/${id}`); load(); };

  return (
    <Shell role="admin">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Languages</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Manage Languages</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-brand-800 hover:bg-brand-700 gap-2 h-11" data-testid="btn-add-language"><Plus className="w-4 h-4"/>Add Language</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Language</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase">Code (e.g. hi, mr)</Label><Input value={form.code} onChange={(e)=>setForm({...form, code:e.target.value})} data-testid="lang-code"/></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase">Name</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="lang-name"/></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase">Default Message</Label><Textarea value={form.default_message} onChange={(e)=>setForm({...form, default_message:e.target.value})} className="min-h-[100px]" data-testid="lang-msg"/></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={create} className="bg-brand-800 hover:bg-brand-700" data-testid="lang-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Default Message</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`lang-row-${l.code}`}>
                <td className="px-4 py-3 font-semibold uppercase">{l.code}</td>
                <td className="px-4 py-3">{l.name}</td>
                <td className="px-4 py-3 text-slate-600 max-w-md truncate">{l.default_message}</td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="icon" className="text-rose-600" onClick={() => remove(l.id)} data-testid={`lang-delete-${l.code}`}><Trash2 className="w-4 h-4"/></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-400">No languages</td></tr>}
          </tbody>
        </table>
      </Card>
    </Shell>
  );
}
